import { useMutation } from '@apollo/client';
import { useAtom, useAtomValue } from 'jotai';

import {
  PAYMENT_GATEWAY,
  TEST_RECORD_PAYMENT_TOKEN,
  TEST_RECORD_SUCCEEDED_STATUS,
} from '../../constants';
import { useAnalytics } from '../../context/AnalyticsProvider';
import {
  type Country,
  type CountryHaveEWallet2c2p,
  getSupportedEwallet2C2P,
} from '../../entities/Country';
import type { Currency } from '../../entities/Currency';
import type { Environment, UseCase } from '../../entities/enums';
import { CREATE_2C2P_PAYMENT_INTENT } from '../../graphql';
import { CREATE_2C2P_INVOICE_PAYMENT_INTENT } from '../../graphql/Mutations';
import { useGenerateIdempotencyKey } from '../../hook/useGenerateIdempotencyKey';
import type {
  HirerPaymentResult,
  ICreate2C2PPaymentIntentForInvoicePayload,
  ICreate2C2PPaymentIntentPayload,
  Invoice,
  PaymentStatus,
} from '../../types';
import {
  redirectCartidQuery,
  redirectEWalletQuery,
  redirectOrderidQuery,
} from '../../utils/constants';
import getReturnUrl from '../../utils/getReturnUrl';
import { handleOrderErrorPayload } from '../../utils/handleOrderError';
import { PaymentDefaultError } from '../Errors/customErrors';
import {
  hideHkEWalletAtom,
  hideIdEwalletAtom,
  hidePhEWalletAtom,
} from '../Maintenance/MaintenanceStore';

import {
  accountNoOVOAtom,
  eWalletsOptionAtom,
  selectedEWalletOptionAtom,
  validAccNoOVOAtom,
  validEwalletAtom,
} from './EWalletStore';

interface InvoiceInputs {
  customerId: string;
  accountId: string;
  prn: string;
  invoice: Invoice[];
  email: string;
  totalForPaymentIntent: number;
}

interface CajaInputs {
  cartId: string;
  referenceId: string;
}

export interface UseEWalletInput<T> {
  useCase: UseCase;
  currency: Currency;
  country: Country;
  env: Environment;
  invoiceInputs: T extends 'INVOICE' ? InvoiceInputs : undefined;
  cajaInputs: T extends 'CAJA' ? CajaInputs : undefined;
}

const useEwallet = ({
  useCase,
  currency,
  country,
  env,
  invoiceInputs = {
    customerId: '',
    accountId: '',
    prn: '',
    invoice: [],
    email: '',
    totalForPaymentIntent: 0,
  },
  cajaInputs = {
    cartId: '',
    referenceId: '',
  },
}: UseEWalletInput<UseCase>) => {
  const { customerId, accountId, prn, invoice, email, totalForPaymentIntent } =
    invoiceInputs;
  const { cartId, referenceId } = cajaInputs;
  const { sendViewAlertViewedEvent } = useAnalytics();

  const [create2C2PPaymentIntent] = useMutation(CREATE_2C2P_PAYMENT_INTENT);
  const [create2C2PPaymentIntentForInvoices] = useMutation(
    CREATE_2C2P_INVOICE_PAYMENT_INTENT,
  );

  const supportedEwallets =
    getSupportedEwallet2C2P[country as CountryHaveEWallet2c2p];

  const [eWalletsOption, setEWalletsOption] = useAtom(eWalletsOptionAtom);
  setEWalletsOption(supportedEwallets);

  const [selectedEWalletOption, setSelectedEWalletOption] = useAtom(
    selectedEWalletOptionAtom,
  );

  const [validEwallet, setValidEwallet] = useAtom(validEwalletAtom);

  const [accountNoOVO, setAccountNoOVO] = useAtom(accountNoOVOAtom);

  const [validAccNoOVO, setValidAccNoOVO] = useAtom(validAccNoOVOAtom);

  const isMaintenanceId = useAtomValue(hideIdEwalletAtom);
  const isMaintenancePh = useAtomValue(hidePhEWalletAtom);
  const isMaintenanceHk = useAtomValue(hideHkEWalletAtom);

  const { generateNewIdempotencyKey } = useGenerateIdempotencyKey();

  const ACCOUNT_NO_REGEX = new RegExp('^[0-9]+$');

  const handleOnChangeEWallet = (e: any) => {
    generateNewIdempotencyKey();
    if (e.target && e.target.value) {
      setSelectedEWalletOption(e.target.value);
    }
  };

  const handleOnChangeOVOEWallet = (e: any) => {
    const accountNumber = e.target?.value;
    const isValidAccountNumber = ACCOUNT_NO_REGEX.test(accountNumber);
    setValidAccNoOVO(!isValidAccountNumber);
    if (isValidAccountNumber || accountNumber === '') {
      setAccountNoOVO(accountNumber);
    }
  };

  const submitEWalletPayment = async (): Promise<HirerPaymentResult> => {
    if (selectedEWalletOption === '') {
      setValidEwallet(true);
      return { status: 'requires_action' };
    }

    if (
      (isMaintenanceId && currency === 'IDR') ||
      (isMaintenancePh && currency === 'PHP') ||
      (isMaintenanceHk && currency === 'HKD')
    ) {
      throw new PaymentDefaultError('Payment method is in maintenance');
    }

    if (
      selectedEWalletOption === 'OVO' &&
      !ACCOUNT_NO_REGEX.test(accountNoOVO)
    ) {
      setValidAccNoOVO(true);
      sendViewAlertViewedEvent(
        [
          {
            type: 'banner',
            title: 'Please enter a valid mobile number',
            messageCode: 'invalid_mobile_number_error',
          },
        ],
        'IdEWallet',
      );

      return { status: 'requires_action' };
    }

    if (currency !== 'IDR' && currency !== 'PHP' && currency !== 'HKD') {
      return { status: 'requires_action' };
    }

    const frontendReturnUrl = getReturnUrl(
      PAYMENT_GATEWAY['2c2p'],
      [
        redirectEWalletQuery,
        redirectCartidQuery(cartId),
        redirectOrderidQuery(referenceId),
      ],
      useCase,
      env,
    );

    const inputVariables: ICreate2C2PPaymentIntentPayload = {
      orderId: referenceId,
      paymentMethodType: 'DPAY',
      frontendReturnUrl,
      channelCode: selectedEWalletOption,
    };

    if (selectedEWalletOption === 'OVO') {
      inputVariables.accountNo = accountNoOVO;
    }

    const paymentIntent = await create2C2PPaymentIntent({
      variables: {
        input: inputVariables,
      },
    });

    const paymentIntentResponse = paymentIntent.data?.create2C2PPaymentIntent;
    const paymentLink = paymentIntentResponse?.paymentLink;
    const paymentToken = paymentIntentResponse?.paymentToken;

    // Scalability: skip payment gateway call
    if (paymentToken === TEST_RECORD_PAYMENT_TOKEN) {
      return {
        status: 'succeeded' as PaymentStatus,
        paymentIntentId: TEST_RECORD_SUCCEEDED_STATUS,
      };
    }

    const eWalletData = {
      paymentLink,
      paymentToken,
    };

    localStorage.setItem('processingPaymentData', JSON.stringify(eWalletData));

    if (paymentIntentResponse.__typename !== 'PaymentIntent2C2PPayload') {
      handleOrderErrorPayload(
        paymentIntentResponse.__typename,
        paymentIntentResponse.message,
      );
      throw new PaymentDefaultError(paymentIntentResponse.message);
    }

    if (!paymentLink) {
      throw new PaymentDefaultError(paymentIntentResponse?.message);
    }

    window.location.assign(paymentIntentResponse.paymentLink);

    return { status: 'succeeded' };
  };

  const submitEWalletPaymentForInvoices =
    async (): Promise<HirerPaymentResult> => {
      if (selectedEWalletOption === '') {
        setValidEwallet(true);
        return { status: 'requires_action' };
      }

      if (
        (isMaintenanceId && currency === 'IDR') ||
        (isMaintenancePh && currency === 'PHP') ||
        (isMaintenanceHk && currency === 'HKD')
      ) {
        throw new PaymentDefaultError('Payment method is in maintenance');
      }

      if (
        selectedEWalletOption === 'OVO' &&
        !ACCOUNT_NO_REGEX.test(accountNoOVO)
      ) {
        setValidAccNoOVO(true);
        sendViewAlertViewedEvent(
          [
            {
              type: 'banner',
              title: 'Please enter a valid mobile number',
              messageCode: 'invalid_mobile_number_error',
            },
          ],
          'IdEWallet',
        );
        return { status: 'requires_action' };
      }

      if (currency !== 'IDR' && currency !== 'PHP' && currency !== 'HKD') {
        return { status: 'requires_action' };
      }

      const frontendReturnUrl = getReturnUrl(
        PAYMENT_GATEWAY['2c2p'],
        [redirectEWalletQuery],
        useCase,
        env,
      );

      const invoices = invoice!.map(({ id, ...rest }) => ({
        reference: id,
        ...rest,
      }));

      const inputVariables: ICreate2C2PPaymentIntentForInvoicePayload = {
        seekBillingAccountId: customerId, // TODO: need to update graphql to take in customerId instead of seekBillingAccountId
        seekAdvertiserId: accountId, // TODO: need to update graphql to take in accountId instead of seekAdvertiserId
        paymentMethodType: 'DPAY',
        channelCode: selectedEWalletOption,
        totalForPaymentIntent,
        invoices,
        email,
        prn,
        frontendReturnUrl,
      };

      if (selectedEWalletOption === 'OVO') {
        inputVariables.accountNo = accountNoOVO;
      }

      const paymentIntent = await create2C2PPaymentIntentForInvoices({
        variables: {
          input: inputVariables,
        },
      });

      const paymentIntentResponse =
        paymentIntent.data?.create2C2PPaymentIntentForInvoices;
      const paymentLink = paymentIntentResponse?.paymentLink;
      const paymentToken = paymentIntentResponse?.paymentToken;

      // Scalability: skip payment gateway call
      if (paymentToken === TEST_RECORD_PAYMENT_TOKEN) {
        return {
          status: 'succeeded' as PaymentStatus,
          paymentIntentId: TEST_RECORD_SUCCEEDED_STATUS,
        };
      }

      const eWalletData = {
        paymentLink,
        paymentToken,
      };

      localStorage.setItem(
        'processingPaymentData',
        JSON.stringify(eWalletData),
      );

      if (!paymentLink) {
        throw new PaymentDefaultError(paymentIntentResponse?.message);
      }

      window.location.assign(paymentIntentResponse.paymentLink);

      return {
        status: 'succeeded' as PaymentStatus,
      };
    };

  return {
    accountNoOVO,
    country,
    currency,
    eWalletsOption,
    selectedEWalletOption,
    validEwallet,
    validAccNoOVO,
    handleOnChangeEWallet,
    handleOnChangeOVOEWallet,
    submitEWalletPayment,
    submitEWalletPaymentForInvoices,
  };
};

export default useEwallet;
